<template>
  <div class="flex flex-row max-w-screen-xl w-full py-2">
    <!-- START DESKTOP SIDE NAV -->
    <div class="hidden md:flex flex-col bg-white rounded w-1/3 py-5 panel" v-if="currentForm">
      <div class="flex flex-row justify-between px-3 md:px-10">
        <div class="flex items-center">
          <SecondaryButton @click="previewForm">
            <div class="flex flex-row">
              <div class="flex items-center mr-1">
                <EyeIcon class="w-6 h-6 text-brand-green cursor-pointer" />
              </div>
              <div class="flex items-center">
                <p>Preview</p>
              </div>
            </div>
          </SecondaryButton>
        </div>

        <div class="flex items-center">
          <PrimaryButton
            @click="saveForm"
            :is-loading="isApplicationTemplateLoading"
            :disabled="isApplicationTemplateLoading"
            >Save form</PrimaryButton
          >
        </div>
      </div>

      <div class="px-10">
        <Line />
      </div>

      <template v-if="sidePanelState === 'add'">
        <div class="flex flex-col px-10 mb-3">
          <p class="font-semibold py-0.5">Question Fields</p>
          <p class="py-0.5">Drag an input into the editor to place it into your form.</p>
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col px-10 mb-3">
          <p class="font-semibold py-0.5">Form Sections</p>
          <p class="py-0.5">Drag the sections to sort it up or down.</p>
        </div>
      </template>

      <div class="flex flex-row py-1 px-10 justify-between">
        <div
          class="flex items-center py-1"
          :class="[
            'cursor-pointer',
            sidePanelState === 'add' ? 'border-b-2 border-brand-green' : ''
          ]"
          @click="setSidePanel('add')"
        >
          <PlusIcon
            :class="[
              'w-5 h-5 cursor-pointer mr-1',
              sidePanelState === 'add' ? 'text-brand-green font-semibold' : 'text-brand-dark-gray'
            ]"
          />
          <p
            :class="[
              'cursor-pointer',
              sidePanelState === 'add' ? 'text-brand-green' : 'text-brand-dark-gray'
            ]"
          >
            Add fields
          </p>
        </div>

        <div
          class="flex items-center py-1"
          :class="[
            'cursor-pointer',
            sidePanelState === 'content' ? 'border-b-2 border-brand-green' : ''
          ]"
          @click="setSidePanel('content')"
        >
          <p
            :class="[
              'cursor-pointer',
              sidePanelState === 'content'
                ? 'text-brand-green font-semibold'
                : 'text-brand-dark-gray'
            ]"
          >
            Application content
          </p>
        </div>
      </div>

      <template v-if="sidePanelState === 'content'">
        <div class="flex flex-col py-3 pr-3 h-full overflow-y-auto">
          <Draggable
            class="dragArea list-group w-full"
            :list="currentForm ? currentForm.sections : []"
            ghost-class="drop-target-field"
            chosen-class="moving-section"
            :animation="200"
            group="sections"
            :preventOnFilter="false"
            :invert-swap="true"
          >
            <div
              v-for="(section, sectionIndex) in currentForm.sections"
              :key="`${section.section_id}-${sectionIndex}-${section.fields.length}`"
              @click="setCurrentSection(sectionIndex)"
            >
              <div class="hidden w-full border-2 border-brand-green target-line my-2"></div>
              <div
                class="flex pr-2 cursor-pointer rounded-r-md hover:bg-brand-light-gray draggable-item"
                :class="[
                  currentSectionIndex === sectionIndex
                    ? 'border-l-4 border-brand-green bg-brand-light-gray '
                    : ''
                ]"
              >
                <div class="flex flex-col w-full py-1 pl-8 pr-2">
                  <h5 class="py-1 font-semibold">{{ section.label }}</h5>
                  <p
                    class="py-1 pl-6 text-sm text-brand-dark-gray truncate"
                    v-for="field in section.fields"
                    :key="`side-panel-${field.field_id}`"
                  >
                    {{ field.label }}
                  </p>
                </div>
              </div>
            </div>
          </Draggable>
        </div>
      </template>

      <template v-else>
        <div class="flex flex-col py-3 px-5 h-full overflow-y-auto">
          <h5 class="py-3">Question Fields</h5>
          <Draggable
            class="dragArea list-group w-full"
            :list="
              fieldDefaults.filter((f) => f.display_in_builder && f.field_id !== 'text_display')
            "
            ghost-class="drop-target-default-field"
            chosen-class="moving-default-field"
            :animation="200"
            :sort="false"
            :preventOnFilter="false"
            :invert-swap="true"
            :group="{ name: 'fields', pull: 'clone', put: false }"
            :clone="handleFieldClone"
          >
            <div
              v-for="field in fieldDefaults.filter(
                (f) => f.display_in_builder && f.field_id !== 'text_display'
              )"
              :key="`side-panel-field-${field.field_id}`"
            >
              <div
                v-if="
                  field.field_id !== 'agent' ||
                  (getCurrentUser.landlord.agent_field_enabled &&
                    getCurrentUser.landlord.in_company)
                "
                class="py-2"
              >
                <AddFieldOption :field="field" />
              </div>
            </div>
          </Draggable>

          <h5 class="py-3">Format Elements</h5>
          <Draggable
            class="dragArea list-group w-full"
            :list="fieldDefaults.filter((f) => f.field_id === 'text_display')"
            ghost-class="drop-target-default-field"
            chosen-class="moving-default-field"
            :animation="200"
            :sort="false"
            :preventOnFilter="false"
            :invert-swap="true"
            :group="{ name: 'fields', pull: 'clone', put: false }"
          >
            <div
              v-for="field in fieldDefaults.filter((f) => f.field_id === 'text_display')"
              :key="`side-panel-field-${field.field_id}`"
              class="py-2"
            >
              <SecondaryButton :classes="'border-brand-mid-gray'">
                <div class="flex items-center px-8">
                  <img src="@/assets/icons/text_field.svg" class="w-5 h-5 mr-2" />
                  <span class="text-brand-green">{{ field.label }}</span>
                </div>
              </SecondaryButton>
            </div>
          </Draggable>
          <div class="py-2">
            <SecondaryButton :classes="'border-brand-mid-gray'" @click="createNewSection">
              <div class="flex items-center px-1 md:px-8">
                <img src="@/assets/icons/section.svg" class="w-5 h-5 mr-2" />
                <span class="text-brand-green">Create a New Section</span>
              </div>
            </SecondaryButton>
          </div>
        </div>
      </template>
    </div>
    <!-- END DESKTOP SIDE NAV -->

    <div class="flex flex-col w-full md:w-2/3 md:pl-5 bg-brand-light-gray panel" v-if="currentForm">
      <div
        class="flex flex-col w-full bg-white rounded px-2 py-2 md:px-5 md:py-5 mb-5"
        v-for="(section, index) in currentForm.sections"
        :key="`section-${index}-${section.fields.length}`"
        :ref="`section-${index}`"
      >
        <div
          class="flex flex-row justify-between py-1 w-full relative"
          :ref="`section-${index}-label`"
        >
          <div class="flex w-full items-center">
            <TextDisplayEditToggle
              :helpText="section.label"
              :type="'section_label'"
              @input="section.label = $event.target.value"
            />
          </div>
          <div :ref="`section-${index}-label-anchor`" style="position: absolute; top: 300px"></div>
          <div class="flex justify-end">
            <div
              class="flex md:hidden items-center py-1 px-1 bg-brand-green rounded mr-5"
              @click="openMobileSideMenu(index)"
            >
              <PlusIcon class="w-5 h-5 text-white" />
            </div>

            <div
              class="flex justify-end relative py-0.5 px-0.5"
              :class="[sectionSetting[index] ? 'bg-brand-light-gray rounded' : '']"
            >
              <CogIcon
                class="w-6 h-6 text-brand-dark-gray cursor-pointer"
                @click="toggleSectionSetting(index)"
              />
              <div
                class="absolute flex flex-col top-8 bg-white rounded-lg shadow-lg w-52 z-10"
                v-if="sectionSetting[index]"
              >
                <div
                  class="flex items-center py-2 hover:bg-brand-light-gray py-3 px-3 cursor-pointer"
                  @click="duplicateSection(index)"
                >
                  <DocumentDuplicateIcon class="text-brand-dark-gray w-6 h-6 mr-2" />
                  <p>Duplicate Section</p>
                </div>
                <div
                  class="flex items-center py-2 py-3 px-3"
                  :class="[
                    checkSectionIsEditable(index)
                      ? 'cursor-pointer hover:bg-brand-light-gray'
                      : 'opacity-50 pointer-events-none'
                  ]"
                  @click="removeSection(index)"
                >
                  <TrashIcon class="text-brand-red w-6 h-6 mr-2" />
                  <p>Delete Section</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="py-2">
          <AlertToast v-if="!checkSectionIsEditable(index)">
            <div class="flex flex-col px-3 py-2">
              <div class="flex items-center">
                <p class="font-semibold py-1">
                  Required by
                  <img src="@/assets/images/main-logo.svg" class="w-4 h-4 mx-1 inline-block" />
                  Rent Application
                </p>
              </div>
              <p class="py-1">
                The input fields listed below can not be edited as Rent Application requires this
                information to process applications.
              </p>

              <p
                class="font-semibold py-1"
                v-for="field in sectionRequiredFields(index)"
                :key="`required-field-${field.name}`"
              >
                {{ field.label }}
              </p>
            </div>
          </AlertToast>
        </div>

        <FormEditDraggable
          :fieldList="section.fields"
          @add="addFieldByDraggable(index, $event)"
          @select_type="selectFieldType($event.field, $event.event)"
          @remove="removeField"
          @clone="cloneField"
          @highlight="toggleFieldHighlight"
          @move="moveField($event.direction, index, $event.fieldIndex)"
        />

        <div class="py-2 w-full md:w-1/2 mx-auto">
          <PrimaryButton @click="openFieldOptionModal(index)">Add Field</PrimaryButton>
        </div>
      </div>
    </div>
  </div>
  <ActionToast ref="formEditToast" />
  <FieldOptionModal
    :sectionLabel="currentField ? currentLabel : currentSection.label"
    :showModal="showFieldOptionModal"
    @close="closeFieldOptionModal"
  />
  <MobileSideMenu
    :currentSectionIndex="currentSectionIndex"
    :currentForm="currentForm"
    :showMenu="showMobileSideMenu"
    @close="closeMobileSideMenu"
  />
  <div
    class="md:hidden fixed z-10 left-1 bottom-3 py-2 px-2 rounded bg-brand-green shadow-lg cursor-pointer"
    @click="saveForm"
  >
    <div class="flex items-center">
      <p class="text-white text-sm">Save changes</p>
    </div>
  </div>
</template>
<script>
import _ from "lodash"

import fieldDefaults from "@/constants/fieldDefaults"

import { mapGetters } from "vuex"
import {
  EyeIcon,
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CogIcon
  // StarIcon,
  // ChevronDownIcon,
  // ChevronUpIcon,
  // CheckIcon
  // XIcon
} from "@heroicons/vue/outline"

import AddFieldOption from "./components/AddFieldOption.vue"
import FieldOptionModal from "./components/FieldOptionModal.vue"
import MobileSideMenu from "./components/FormEditMobileSideMenu.vue"
import FormEditDraggable from "./components/FormEditDraggable.vue"

// import { Switch } from "@headlessui/vue"

import { VueDraggableNext } from "vue-draggable-next"

import { APPLICATION_TEMPLATE_UPDATE_FORM, APPLICATION_TEMPLATE_SET_TEMPORARY } from "@/store/types"

export default {
  components: {
    EyeIcon,
    PlusIcon,
    Draggable: VueDraggableNext,
    TrashIcon,
    DocumentDuplicateIcon,
    CogIcon,
    AddFieldOption,
    FieldOptionModal,
    MobileSideMenu,
    FormEditDraggable
    // XIcon,
    // Switch,
    // StarIcon,
    // ChevronDownIcon,
    // ChevronUpIcon,
    // CheckIcon,
  },
  computed: {
    ...mapGetters([
      "getCurrentApplicationTemplate",
      "getApplicationTemplateTemporary",
      "isApplicationTemplateLoading",
      "getCurrentUser"
    ]),
    currentSection() {
      if (!this.currentForm) return {}

      return this.currentForm.sections[this.currentSectionIndex]
    }
  },
  created() {
    const that = this
    if (
      this.getApplicationTemplateTemporary &&
      this.getCurrentApplicationTemplate.slug === this.getApplicationTemplateTemporary.slug
    ) {
      this.currentForm = _.cloneDeep(this.getApplicationTemplateTemporary.form)
    }

    this.$emitter.on("dashboard_form_edit_mobile_side_menu", (action) => {
      if (action === "open") {
        that.openMobileSideMenu(0)
      }
    })
  },
  mounted() {
    this.fieldDefaults = [...fieldDefaults]
  },
  data() {
    return {
      sidePanelState: "content", //'add'
      currentSectionIndex: 0,
      currentForm: null,
      showFieldOptionModal: false,
      sectionSetting: {},
      showMobileSideMenu: false,
      fieldDefaults: [],
      currentField: null,
      currentListType: "",
      currentLabel: ""
    }
  },
  watch: {
    getCurrentApplicationTemplate: {
      handler(val) {
        if (val) {
          this.currentForm = _.cloneDeep(val.form)
        }
      },
      deep: true,
      immediate: true
    },
    currentForm: {
      handler(val) {
        if (val) {
          this.$store.commit(APPLICATION_TEMPLATE_SET_TEMPORARY, {
            ...this.getCurrentApplicationTemplate,
            form: val
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    handleFieldClone(field) {
      const newField = _.cloneDeep(field)
      newField.name = `${newField.field_id}-${this.randomSlug()}`
      return newField
    },
    previewForm() {
      this.$store.commit(APPLICATION_TEMPLATE_SET_TEMPORARY, {
        ...this.getCurrentApplicationTemplate,
        form: this.currentForm
      })
      this.$router.push({
        name: "apply_form",
        params: { slug: this.getCurrentApplicationTemplate.slug },
        query: { preview: true }
      })
    },
    async saveForm() {
      try {
        await this.$store.dispatch(APPLICATION_TEMPLATE_UPDATE_FORM, {
          slug: this.getCurrentApplicationTemplate.slug,
          form: this.currentForm
        })
        this.$refs.formEditToast.show({
          type: "success",
          hasClose: true,
          content: "Form saved"
        })
      } catch (err) {
        this.$refs.formEditToast.show({
          type: "danger",
          hasClose: true,
          content: "Error has occured."
        })
      }
    },
    openMobileSideMenu(index) {
      this.currentSectionIndex = index
      this.showMobileSideMenu = true
    },
    closeMobileSideMenu(data) {
      this.showMobileSideMenu = false

      if (data) {
        this.addField(data.sectionIndex, data.field)

        const section = this.currentForm.sections[data.sectionIndex]
        const fieldIndex = section.fields.length - 1
        data.field.highlight = true

        setTimeout(() => {
          data.field.highlight = false
        }, 4000)

        setTimeout(() => {
          this.$refs[`section-${data.sectionIndex}-field-${fieldIndex}-draggable`].scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest"
          })
        }, 10)
      }
    },
    openFieldOptionModal(index, field, type) {
      this.currentSectionIndex = index

      if (field) {
        this.currentField = field
        this.currentListType = type
        this.currentLabel = `${field.label} - ${type} Option Field List`
      }
      this.showFieldOptionModal = true
    },
    closeFieldOptionModal(type) {
      this.showFieldOptionModal = false

      if (this.currentField) {
        this.addFieldToYesNoFieldFromModal(type)
        this.currentField = null
        this.currentListType = ""
        this.currentLabel = ""
      } else {
        this.addFieldByButton(type)
      }
    },
    randomSlug() {
      return _.times(5, () => _.random(35).toString(36)).join("")
    },
    toggleSectionSetting(index) {
      this.sectionSetting[index] = !this.sectionSetting[index]
    },
    setSidePanel(state) {
      this.sidePanelState = state
    },
    setCurrentSection(index) {
      this.currentSectionIndex = index
      const that = this
      setTimeout(() => {
        that.$refs[`section-${index}-label-anchor`][0].scrollIntoView({
          behavior: "smooth",
          block: "end"
        })
      }, 10)
    },
    sectionRequiredFields(sectionIndex) {
      const that = this
      const section = this.currentForm.sections[sectionIndex]

      return section.fields
        .map((f) => {
          if (!that.checkFieldIsEditable(f) || that.checkFieldIsRequired(f)) {
            return f
          }
        })
        .filter(Boolean)
    },
    checkSectionIsEditable(sectionIndex) {
      const section = this.currentForm.sections[sectionIndex]
      const that = this

      return section.fields
        .map((f) => that.checkFieldIsEditable(f) && !that.checkFieldIsRequired(f))
        .every(Boolean)
    },
    checkFieldIsEditable(field) {
      return field.editable !== false
    },
    checkFieldIsRequired(field) {
      return _.chain(this.getCurrentApplicationTemplate.selected_report_sources)
        .map("required_fields")
        .flatten()
        .uniq()
        .value()
        .includes(field.name)
    },
    createNewSection() {
      this.currentForm.sections.splice(this.currentSectionIndex + 1, 0, {
        label: "New Section",
        fields: [],
        section_id: `custom-section-${this.randomSlug()}`
      })
      this.setCurrentSection(this.currentSectionIndex + 1)
    },
    duplicateSection(sectionIndex) {
      const that = this
      this.sectionSetting[sectionIndex] = false
      const duplicated = _.cloneDeep(this.currentForm.sections[sectionIndex])

      duplicated.fields.forEach((f) => {
        f.name = `${f.field_id}-${that.randomSlug()}`
        f.editable = true
      })

      this.currentForm.sections.splice(sectionIndex + 1, 0, duplicated)
      this.setCurrentSection(sectionIndex + 1)

      this.$refs.formEditToast.show({
        type: "success",
        hasClose: true,
        content: "Section duplicated"
      })
    },
    removeSection(sectionIndex) {
      this.sectionSetting[sectionIndex] = false
      _.pullAt(this.currentForm.sections, sectionIndex)
      this.$refs.formEditToast.show({
        type: "danger",
        hasClose: true,
        content: "Section deleted"
      })
    },
    removeField(field) {
      if (!this.checkFieldIsRequired(field) && this.checkFieldIsEditable(field)) {
        this.currentForm.sections = this.currentForm.sections.map((s) => {
          const fields = s.fields.filter((f) => f.name !== field.name)
          s.fields = fields
          return s
        })
      }
    },
    addFieldToYesNoFieldFromModal(type) {
      if (type && type !== "yes_no") {
        const newField = _.cloneDeep(this.fieldDefaults.find((f) => f.field_id === type))
        newField.name = `${newField.field_id}-${this.randomSlug()}`

        this.currentField[`${this.currentListType}_fields`] = [
          ...this.currentField[`${this.currentListType}_fields`],
          newField
        ]
      }
    },
    addField(sectionIndex, field) {
      const newField = _.cloneDeep(this.fieldDefaults.find((f) => f.field_id === field.field_id))
      newField.name = `${newField.field_id}-${this.randomSlug()}`

      const section = this.currentForm.sections[sectionIndex]
      section.fields = [...section.fields, newField]
    },
    cloneField(field) {
      const newField = _.cloneDeep(field)
      newField.name = `${newField.field_id}-${this.randomSlug()}`
      newField.editable = true

      this.currentForm.sections.forEach((s) => {
        const index = _.findIndex(s.fields, (f) => f.name === field.name)

        if (index >= 0) {
          s.fields.splice(index + 1, 0, newField)
        }
      })
    },
    selectFieldType(field, type) {
      const newField = _.cloneDeep(this.fieldDefaults.find((f) => f.field_id === type.field_id))
      newField.name = `${newField.field_id}-${this.randomSlug()}`

      this.currentForm.sections.forEach((s) => {
        const index = _.findIndex(s.fields, (f) => f.name === field.name)

        if (index >= 0) {
          s.fields[index] = newField
        }
      })
    },
    addFieldByButton(type) {
      if (type) {
        const newField = _.cloneDeep(this.fieldDefaults.find((f) => f.field_id === type))
        newField.name = `${newField.field_id}-${this.randomSlug()}`

        const section = this.currentSection
        section.fields = [...section.fields, newField]
      }
    },
    addFieldByDraggable(sectionIndex, evt) {
      // Set unique field name
      if (evt.added) {
        const section = this.currentForm.sections[sectionIndex]
        const newField = section.fields[evt.added.newIndex]
        newField.name = `${newField.field_id}-${this.randomSlug()}`
      }
    },
    addFieldToYesNoField(listType, field, evt) {
      if (evt.added) {
        const newField = field[`${listType}_fields`][evt.added.newIndex]
        newField.name = `${newField.field_id}-${this.randomSlug()}`

        //Prevent add of yes no field to current yes no field
        if (newField.field_id === "yes_no") {
          field[`${listType}_fields`] = [
            ...field[`${listType}_fields`].filter((f) => f.name !== newField.name)
          ]
        }
      }
    },
    selectYesNoFieldType(listType, contextField, field, newType) {
      const newField = _.cloneDeep(this.fieldDefaults.find((f) => f.field_id === newType.field_id))
      newField.name = `${newField.field_id}-${this.randomSlug()}`

      const index = _.findIndex(contextField[`${listType}_fields`], (f) => f.name === field.name)

      if (index >= 0) {
        contextField[`${listType}_fields`][index] = newField
      }
    },
    removeYesNoField(listType, contextField, field) {
      if (!this.checkFieldIsRequired(field) && this.checkFieldIsEditable(field)) {
        contextField[`${listType}_fields`] = [
          ...contextField[`${listType}_fields`].filter((f) => f.name !== field.name)
        ]
      }
    },
    cloneYesNoField(listType, contextField, field) {
      const newField = _.cloneDeep(field)
      newField.name = `${newField.field_id}-${this.randomSlug()}`
      newField.editable = true

      const index = _.findIndex(contextField[`${listType}_fields`], (f) => f.name === field.name)

      if (index >= 0) {
        contextField[`${listType}_fields`].splice(index + 1, 0, newField)
      }
    },
    toggleFieldHighlight(field) {
      field.highlight = !field.highlight

      if (field.highlight) {
        this.currentForm.sections.forEach((s) => {
          s.fields.forEach((f) => {
            if (f.name !== field.name) {
              f.highlight = false
            }
          })
        })
      }
    },
    moveField(direction, sectionIndex, fieldIndex) {
      const section = this.currentForm.sections[sectionIndex]
      const field = section.fields[fieldIndex]

      if (direction === "up") {
        if (fieldIndex === 0) {
          if (sectionIndex === 0) {
            return
          } else {
            this.currentForm.sections[sectionIndex - 1].fields = [
              ...this.currentForm.sections[sectionIndex - 1].fields,
              field
            ]
            section.fields = [...section.fields.slice(1)]
          }
        } else {
          section.fields = [
            ...section.fields.slice(0, fieldIndex - 1),
            field,
            section.fields[fieldIndex - 1],
            ...section.fields.slice(fieldIndex + 1)
          ]
        }
      } else {
        if (fieldIndex === section.fields.length - 1) {
          if (sectionIndex === this.currentForm.sections.length - 1) {
            return
          } else {
            this.currentForm.sections[sectionIndex + 1].fields = [
              field,
              ...this.currentForm.sections[sectionIndex + 1].fields
            ]
            section.fields = [...section.fields.slice(0, section.fields.length - 1)]
          }
        } else {
          section.fields = [
            ...section.fields.slice(0, fieldIndex),
            section.fields[fieldIndex + 1],
            section.fields[fieldIndex],
            ...section.fields.slice(fieldIndex + 2)
          ]
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.panel {
  max-height: 80vh;
  overflow-y: scroll;
}
@media (max-width: 768px) {
  .panel {
    max-height: 100vh;
  }
}
</style>
<style lang="scss">
.moving-section {
  @apply opacity-100;

  .draggable-item {
    @apply opacity-100 bg-white rounded border border-brand-green;
  }
}

.moving-field {
  .draggable-item {
    @apply opacity-50 bg-gray-100 border-brand-green;
  }
}

.moving-default-field {
  .secondary-button {
    @apply opacity-50 border-brand-green;
  }
}

.drop-target-field {
  .target-line {
    @apply block opacity-100;
  }
  .draggable-item {
    @apply hidden;
  }
}
</style>
